import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory } from 'react-router-dom'
import styled, { css, keyframes } from 'styled-components'
import { connect, useSelector } from 'react-redux'
import { AppState } from '../../store'
import { HelpCenterArticle, PublicLocale } from '../../../types'
import HelpCenterController from '../../controllers/HelpCenterController'
import MobilityHelper from '../../../helpers/MobilityHelper'
import Loader from '../Loader/Loader'
import { useDebouncedCallback } from 'use-debounce'
import { articleLink, overviewLink } from '../../helpers/NavigationHelper'
import { setLocale } from '../../store/help-center/actions'
import Utils from '../../../utilities/Utils'
import HelpCenterHelper from '../../../helpers/HelpCenterHelper'

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`

const Search = styled.input`
  display: flex;
  width: 100%;
  padding: 16px 16px 16px 48px;
  background-color: transparent;
  outline: none;
  border-width: 1px;
  border-color: rgba(0, 0, 0, .08);
  border-radius: 10px;
  color: black;
  background: rgba(255, 255, 255, 0.2);
  transition-property: color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter, -webkit-backdrop-filter;
  transition-duration: 0.15s;
  transition-timing-function: linear;
  font-size: 18px;
  line-height: 28.8px;
  font-weight: 400;
  
  &:hover {
    box-shadow: 0 0 #0000, 0 0 #0000, 0 5px 10px rgba(0,0,0,0.10);
  }

  &:focus {
    background: white;
  }
`

const Container = styled.div<{ backgroundImageUrl?: string, height: number, fade: boolean }>`
  min-height: 172px;
  height: ${props => props.height}px;
  padding: 24px 40px 40px 40px;
  background-position: 50%;
  background-size: cover;
  background-color: ${props => props.theme.colors.headerBackground};
  
  ${props => props.backgroundImageUrl && css`
    background-image: url(${props.backgroundImageUrl});

    ${Search} {
      background: white;
    }
  `}

  ${props => props.fade && css`
    background: radial-gradient(333.38% 100% at 50% 0%,
      rgba(${(props) => Utils.hexToRgb(props.theme.colors.body)}, 0) 0%,
      rgba(${(props) => Utils.hexToRgb(props.theme.colors.body)}, .00925356) 11.67%,
      rgba(${(props) => Utils.hexToRgb(props.theme.colors.body)}, .0337355) 21.17%,
      rgba(${(props) => Utils.hexToRgb(props.theme.colors.body)}, .0718242) 28.85%,
      rgba(${(props) => Utils.hexToRgb(props.theme.colors.body)}, .121898) 35.03%,
      rgba(${(props) => Utils.hexToRgb(props.theme.colors.body)}, .182336) 40.05%,
      rgba(${(props) => Utils.hexToRgb(props.theme.colors.body)}, .251516) 44.25%,
      rgba(${(props) => Utils.hexToRgb(props.theme.colors.body)}, .327818) 47.96%,
      rgba(${(props) => Utils.hexToRgb(props.theme.colors.body)}, .409618) 51.51%,
      rgba(${(props) => Utils.hexToRgb(props.theme.colors.body)}, .495297) 55.23%,
      rgba(${(props) => Utils.hexToRgb(props.theme.colors.body)}, .583232) 59.47%,
      rgba(${(props) => Utils.hexToRgb(props.theme.colors.body)}, .671801) 64.55%,
      rgba(${(props) => Utils.hexToRgb(props.theme.colors.body)}, .759385) 70.81%,
      rgba(${(props) => Utils.hexToRgb(props.theme.colors.body)}, .84436) 78.58%,
      rgba(${(props) => Utils.hexToRgb(props.theme.colors.body)}, .9551) 88.2%,
      rgba(${(props) => Utils.hexToRgb(props.theme.colors.body)}, 1) 100%),
      url(${props.backgroundImageUrl}),
      ${props.theme.colors.headerBackground};
  `}

  @media screen and (max-width: 767px) {
    padding-left: 20px;
    padding-right: 20px;
    height: fit-content;
  }
`

const Wrapper = styled.div`
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
`

const Subheader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 24px;
`

const Logo = styled(Link)`
  display: inline-block;
  text-decoration: none;
  flex: 1;

  img {
    max-width: 360px;
    max-height: 35px;
    vertical-align: baseline;
  }

  span {
    font-weight: bold;
    font-size: 20px;
    color: ${props => props.theme.colors.headerColor};
  }
`

const HamburgerMenu = styled.div`
  display: none;
  padding: 6px;
  cursor: pointer;

  svg {
    fill: ${props => props.theme.colors.headerColor};
  }

  @media screen and (max-width: 767px) {
    display: flex;
  }
`

const LanguagePicker = styled.div`
  position: relative;

  @media screen and (max-width: 767px) {
    padding-left: 20px;
  }
`

const Navigation = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 14px;
  
  a {
    margin: 0 12px;
    text-decoration: none;
    color: ${props => props.theme.colors.headerColor};

    @media screen and (max-width: 767px) {
      display: none;
    }
  }

  ${LanguagePicker} {
    @media screen and (max-width: 767px) {
      display: none;
    }
  }
`

const LanguagePickerSelector = styled.select`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  outline: none;
  opacity: 0;
`

const Language = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  color: ${props => props.theme.colors.headerColor};
`

const WelcomeMessage = styled.div`
  font-size: 28px;
  line-height: 28.8px;
  font-weight: 700;
  color: ${props => props.theme.colors.headerColor};
  margin-bottom: 16px;
  justify-self: flex-start;

  @media screen and (max-width: 767px) {
    font-size: 24px;
    line-height: 24.4px;
  }
`

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;
  position: relative;
`

const SearchInputContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
`

const SearchIcon = styled.svg`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 20px;
`

const SearchResults = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  background: white;
  height: fit-content;
  width: 100%;
  top: 120px;
  border-radius: 10px;
  padding: 12px;
  gap: 8px;
  box-shadow: 0 0 #0000, 0 0 #0000, 0 5px 10px rgba(0,0,0,0.10);
  z-index: 1;
  overflow-y: auto;
  max-height: calc(75vh);
`

const SearchLoader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 150px;
`

const SearchResultsEmpty = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 150px;
`

const SearchResultItem = styled(Link)`
  display: flex;
  flex-direction: column;
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, .08);
  transition: border-color 0.15s linear;
  border-radius: 10px;
  text-decoration: none;

  &:hover {
    border-color: black;
    cursor: pointer;
  }
`

const SearchResultItemTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: black;
  margin-bottom: 8px;
`

const SearchResultItemDescription = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: rgba(0, 0, 0, .6); 
`

const MobileMenu = styled.div<{ active: boolean }>`
  display: none;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  max-height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  background: rgba(0, 0, 0, 0.3);
  width: 100%;
  transition: all 0.3s ease-in-out;
  animation: ${fadeIn} .2s ease-in-out;
  z-index: 1;

  ${props => props.active && css`
    display: flex;
  `}

  ${Language} {
    color: black;
  }
`

const MobileMenuWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  width: 100%;
  padding: 20px;
  padding-top: 0;

  button {
    display: flex;
    justify-content: center;
    padding-top: 24px;
    padding-right: 24px;
    align-self: flex-end;
    align-items: center;
    background-color: transparent;
    border: none;
    cursor: pointer;
  }

  nav {
    display: flex;
    flex-direction: column;

    a {
      color: rgb(34, 34, 34);
      font-size: 15px;
      font-weight: 500;
      margin-bottom: 20px;
      margin-left: 20px;
      margin-right: 20px;
      text-decoration: none;

      &:hover {
        opacity: 0.8;
      }
    }
  }

  ${LanguagePicker} {
    display: initial;

  }
`

const MobileMenuBackground = styled.div`
  flex: 1;
`

interface IDispatchToProps {
  setLocale: typeof setLocale
}

type IProps = {
} & IDispatchToProps

interface IState {
  mobileMenuActive: boolean
  search: string
  searching: boolean
  searchActive: boolean
  articles: HelpCenterArticle[]
}

const Header = (props: IProps) => {
  const { setLocale } = props
  const { currentLocale, helpCenter } = useSelector((state: AppState) => state.helpCenter)
  const history = useHistory()
  const { t } = useTranslation()
  const searchContainer = React.useRef<HTMLDivElement>(null)
  const [state, setState] = React.useState<IState>({
    mobileMenuActive: false,
    search: '',
    searching: false,
    searchActive: false,
    articles: []
  })

  const embedded = HelpCenterHelper.isEmbedded()

  if (embedded) return null

  const { mobileMenuActive, search, searchActive, searching, articles } = state
  const fetchArticles = async (search: string) => {
    if (search.length === 0) {
      setState({ ...state, articles: [] })
      return
    }

    try {
      setState({ ...state, searching: true })
      const articles = await HelpCenterController.search(helpCenter.id, search)

      setState({ ...state, articles: articles, searching: false })
    } catch (ex) {
      console.error(ex)
    }
  }

  const debouncedFetchArticles = useDebouncedCallback(fetchArticles, 500);

  const supportedLocales = helpCenter.supported_locales.map((locale) => {
    return {
      label: t(`PublicLocale::${locale.toLowerCase()}`),
      value: locale,
    }
  })

  const onLocaleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const locale = e.target.value

    if (locale !== currentLocale) {
      // Set store locale
      setLocale(locale as PublicLocale)
      history.push(overviewLink(locale as PublicLocale))
    }
  }

  const onMobileMenuToggle = () => {
    setState({ ...state, mobileMenuActive: !mobileMenuActive })
  }

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const search = e.target.value
    setState({
      ...state,
      search: search,
      searchActive: search.length > 0,
      searching: search.length > 0,
    })
  }

  const onSearchFocus = () => {
    setState({ ...state, searchActive: true })
  }

  const resetSearch = () => {
    setState({
      ...state,
      search: '',
      searchActive: false,
      searching: false,
      articles: [],
    })
  }

  const onOutsideContainerClick = (e) => {
    if (searchContainer.current && !searchContainer.current.contains(e.target)) {
      setState({ ...state, searchActive: false })
    }
  }

  React.useEffect(() => {
    debouncedFetchArticles(search)
  }, [search])

  React.useEffect(() => {
    document.addEventListener('mousedown', onOutsideContainerClick)

    return () => {
      document.removeEventListener('mousedown', onOutsideContainerClick)
    }
  }, [mobileMenuActive])

  const welcomeMessage = MobilityHelper.getTranslation(currentLocale, helpCenter.translations, 'welcome_message')
  const headerLinks = helpCenter.header_links?.filter(link => link.locale === currentLocale) || []

  return (
    <>
      <Container
        backgroundImageUrl={helpCenter.header_image_url}
        height={Number(helpCenter.header_height)}
        fade={helpCenter.header_background_fade}
      >
        <Wrapper>
          <Subheader>
            <Logo to={overviewLink(currentLocale)}>
              {helpCenter.logo_url?.length > 0 && <img src={helpCenter.logo_url} />}
              {helpCenter.header_page_title?.length > 0 && !helpCenter.logo_url && <span>{helpCenter.header_page_title}</span>}
            </Logo>

            <Navigation>
              {headerLinks.map((link, index) => (<a href={link.url} key={index}>{link.label}</a>))}
              <LanguagePicker>
                <LanguagePickerSelector
                  onChange={onLocaleChange}
                  value={currentLocale}
                >
                  {supportedLocales.map((locale) => (<option key={locale.value} value={locale.value}>{locale.label}</option>))}
                </LanguagePickerSelector>

                <Language>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path><path d="M8 15C9.39949 15 10.534 11.866 10.534 8C10.534 4.13401 9.39949 1 8 1C6.60051 1 5.466 4.13401 5.466 8C5.466 11.866 6.60051 15 8 15Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path><path d="M1.448 5.75989H14.524" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path><path d="M1.448 10.2402H14.524" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                  {t(`PublicLocale::${currentLocale}`)}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M5 6.5L8.00093 9.5L11 6.50187" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path></svg>
                </Language>
              </LanguagePicker>

              <HamburgerMenu onClick={onMobileMenuToggle}>
                <svg width="24" height="24" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M1.86861 2C1.38889 2 1 2.3806 1 2.85008C1 3.31957 1.38889 3.70017 1.86861 3.70017H14.1314C14.6111 3.70017 15 3.31957 15 2.85008C15 2.3806 14.6111 2 14.1314 2H1.86861Z"></path><path d="M1 8C1 7.53051 1.38889 7.14992 1.86861 7.14992H14.1314C14.6111 7.14992 15 7.53051 15 8C15 8.46949 14.6111 8.85008 14.1314 8.85008H1.86861C1.38889 8.85008 1 8.46949 1 8Z"></path><path d="M1 13.1499C1 12.6804 1.38889 12.2998 1.86861 12.2998H14.1314C14.6111 12.2998 15 12.6804 15 13.1499C15 13.6194 14.6111 14 14.1314 14H1.86861C1.38889 14 1 13.6194 1 13.1499Z"></path></svg>
              </HamburgerMenu>
            </Navigation>
          </Subheader>

          <SearchContainer ref={searchContainer}>
            {welcomeMessage?.length > 0 && <WelcomeMessage>
              {welcomeMessage}
            </WelcomeMessage>}
            <SearchInputContainer>
              <SearchIcon width="22" height="21" viewBox="0 0 22 21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fillRule="evenodd" clipRule="evenodd" d="M3.27485 8.7001C3.27485 5.42781 5.92757 2.7751 9.19985 2.7751C12.4721 2.7751 15.1249 5.42781 15.1249 8.7001C15.1249 11.9724 12.4721 14.6251 9.19985 14.6251C5.92757 14.6251 3.27485 11.9724 3.27485 8.7001ZM9.19985 0.225098C4.51924 0.225098 0.724854 4.01948 0.724854 8.7001C0.724854 13.3807 4.51924 17.1751 9.19985 17.1751C11.0802 17.1751 12.8176 16.5627 14.2234 15.5265L19.0981 20.4013C19.5961 20.8992 20.4033 20.8992 20.9013 20.4013C21.3992 19.9033 21.3992 19.0961 20.9013 18.5981L16.0264 13.7233C17.0625 12.3176 17.6749 10.5804 17.6749 8.7001C17.6749 4.01948 13.8805 0.225098 9.19985 0.225098Z"></path></SearchIcon>
              <Search
                placeholder={t('Header::Search for articles...')}
                value={search}
                onChange={onSearchChange}
                onFocus={onSearchFocus}
              />
            </SearchInputContainer>

            {search?.length > 0 && searchActive && <SearchResults>
              {searching && <SearchLoader><Loader /></SearchLoader>}
              {!searching && <>
                {articles.length === 0 && <SearchResultsEmpty>{t('Header::No results found for "{{search}}"', { search: search })}</SearchResultsEmpty>}

                {articles.map(article => (
                  <SearchResultItem key={article.id} to={articleLink(article)} onClick={resetSearch}>
                    <SearchResultItemTitle>
                      {MobilityHelper.getTranslation(currentLocale, article.translations, 'title')}
                    </SearchResultItemTitle>
                    <SearchResultItemDescription>
                      {MobilityHelper.getTranslation(currentLocale, article.translations, 'summary')}
                    </SearchResultItemDescription>
                  </SearchResultItem>
                ))}
              </>}
            </SearchResults>}
          </SearchContainer>
        </Wrapper>
      </Container>

      <MobileMenu active={mobileMenuActive}>
        <MobileMenuWrapper>
          <button onClick={onMobileMenuToggle}>
            <svg width="24" height="24" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M3.5097 3.5097C3.84165 3.17776 4.37984 3.17776 4.71178 3.5097L7.99983 6.79775L11.2879 3.5097C11.6198 3.17776 12.158 3.17776 12.49 3.5097C12.8219 3.84165 12.8219 4.37984 12.49 4.71178L9.20191 7.99983L12.49 11.2879C12.8219 11.6198 12.8219 12.158 12.49 12.49C12.158 12.8219 11.6198 12.8219 11.2879 12.49L7.99983 9.20191L4.71178 12.49C4.37984 12.8219 3.84165 12.8219 3.5097 12.49C3.17776 12.158 3.17776 11.6198 3.5097 11.2879L6.79775 7.99983L3.5097 4.71178C3.17776 4.37984 3.17776 3.84165 3.5097 3.5097Z"></path></svg>
          </button>
          {headerLinks?.length > 0 && <nav>
            {headerLinks.map((link, index) => (<a key={index} href={link.url}>{link.label}</a>))}
          </nav>}
          <LanguagePicker>
            <LanguagePickerSelector
              onChange={onLocaleChange}
              value={currentLocale}
            >
              {supportedLocales.map((locale) => (<option key={locale.value} value={locale.value}>{locale.label}</option>))}
            </LanguagePickerSelector>

            <Language>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path><path d="M8 15C9.39949 15 10.534 11.866 10.534 8C10.534 4.13401 9.39949 1 8 1C6.60051 1 5.466 4.13401 5.466 8C5.466 11.866 6.60051 15 8 15Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path><path d="M1.448 5.75989H14.524" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path><path d="M1.448 10.2402H14.524" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path></svg>
              {t(`PublicLocale::${currentLocale}`)}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M5 6.5L8.00093 9.5L11 6.50187" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path></svg>
            </Language>
          </LanguagePicker>
        </MobileMenuWrapper>
        <MobileMenuBackground onClick={onMobileMenuToggle} />
      </MobileMenu>
    </>
  )
}

const mapDispatchToProps: IDispatchToProps = {
  setLocale,
}


export default connect(null, mapDispatchToProps)(Header)